// components/dashboard/FinancialSummary.jsx
import React from 'react';
import { Card, Row, Col, Progress, Tag, Divider, Typography, Skeleton } from 'antd';
import {
  DollarOutlined,
  RiseOutlined,
  TrophyOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';

const { Title, Text } = Typography;

const FinancialSummary = ({ data, loading = false }) => {
  const calculatePercentageChange = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  const percentageChange = data ? calculatePercentageChange(
    data.thisMonthTurnover,
    data.lastMonthTurnover
  ) : 0;

  const isPositiveChange = percentageChange >= 0;

  // Loading skeleton
  if (loading) {
    return (
      <Card
        title={<Skeleton.Input active size="small" style={{ width: 120 }} />}
        style={{ height: '100%', borderRadius: 8 }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Skeleton.Input active size="large" style={{ width: 180 }} />
          <br />
          <Skeleton.Input active size="small" style={{ width: 120, marginTop: 8 }} />
        </div>
        <Row gutter={16}>
          <Col span={12}>
            <Skeleton active paragraph={{ rows: 2 }} />
          </Col>
          <Col span={12}>
            <Skeleton active paragraph={{ rows: 2 }} />
          </Col>
        </Row>
      </Card>
    );
  }

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.15 }}
    >
      <Card
        title={
          <span>
            <DollarOutlined style={{ marginRight: 8, color: '#059669' }} />
            Mali Ozet
          </span>
        }
        style={{ height: '100%', borderRadius: 8 }}
        className="card-hover-lift"
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Row gutter={[16, 24]}>
            {/* Toplam Ciro */}
            <Col span={24}>
              <motion.div variants={itemVariants} style={{ textAlign: 'center', marginBottom: 8 }}>
                <Title level={2} style={{ margin: 0, color: '#2563eb' }}>
                  <span style={{ fontSize: 18, marginRight: 4 }}>&#x20BA;</span>
                  <CountUp
                    end={data?.totalTurnover || 0}
                    duration={2}
                    decimals={2}
                    separator=","
                    decimal="."
                  />
                </Title>
                <Text type="secondary">Toplam Genel Ciro</Text>
              </motion.div>
              <Divider style={{ margin: '16px 0' }} />
            </Col>

            {/* Aylik Karsilastirma */}
            <Col xs={24} sm={12}>
              <motion.div variants={itemVariants}>
                <Card
                  type="inner"
                  style={{
                    background: 'linear-gradient(135deg, #f6ffed 0%, #e6f7ff 100%)',
                    borderRadius: 8,
                    border: '1px solid #b7eb8f'
                  }}
                  bodyStyle={{ padding: 16 }}
                >
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>Bu Ay</Text>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 600, color: '#059669', marginBottom: 8 }}>
                    <RiseOutlined style={{ marginRight: 8 }} />
                    &#x20BA;
                    <CountUp
                      end={data?.thisMonthTurnover || 0}
                      duration={1.5}
                      decimals={2}
                      separator=","
                      decimal="."
                    />
                  </div>
                  {data?.lastMonthTurnover > 0 && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      <Tag
                        color={isPositiveChange ? 'success' : 'error'}
                        style={{ borderRadius: 10, fontWeight: 500 }}
                      >
                        {isPositiveChange ? '+' : ''}{percentageChange}%
                      </Tag>
                      <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>
                        gecen aya gore
                      </Text>
                    </motion.div>
                  )}
                </Card>
              </motion.div>
            </Col>

            <Col xs={24} sm={12}>
              <motion.div variants={itemVariants}>
                <Card
                  type="inner"
                  style={{
                    background: '#f5f5f5',
                    borderRadius: 8,
                    border: '1px solid #d9d9d9'
                  }}
                  bodyStyle={{ padding: 16 }}
                >
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>Gecen Ay</Text>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 600, color: '#595959' }}>
                    &#x20BA;
                    <CountUp
                      end={data?.lastMonthTurnover || 0}
                      duration={1.5}
                      decimals={2}
                      separator=","
                      decimal="."
                    />
                  </div>
                </Card>
              </motion.div>
            </Col>

            {/* Bugunku Ciro */}
            <Col span={24}>
              <motion.div variants={itemVariants}>
                <Card
                  type="inner"
                  style={{
                    background: 'linear-gradient(90deg, #e6f7ff 0%, #f0f5ff 100%)',
                    borderLeft: '4px solid #2563eb',
                    borderRadius: 8
                  }}
                  bodyStyle={{ padding: 16 }}
                >
                  <Row align="middle" justify="space-between">
                    <Col>
                      <Text type="secondary" style={{ fontSize: 13 }}>Bugunku Ciro</Text>
                      <div style={{ fontSize: 24, fontWeight: 600, color: '#2563eb', marginTop: 4 }}>
                        <DollarOutlined style={{ marginRight: 8 }} />
                        &#x20BA;
                        <CountUp
                          end={data?.todayTurnover || 0}
                          duration={1.5}
                          decimals={2}
                          separator=","
                          decimal="."
                        />
                      </div>
                    </Col>
                    <Col>
                      <motion.div
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          backgroundColor: '#2563eb20',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <DollarOutlined style={{ fontSize: 24, color: '#2563eb' }} />
                      </motion.div>
                    </Col>
                  </Row>
                </Card>
              </motion.div>
            </Col>

            <Col span={24}>
              <Divider style={{ margin: '8px 0 16px' }} />
            </Col>

            {/* Siparis Degeri Istatistikleri */}
            <Col span={24}>
              <motion.div variants={itemVariants}>
                <Title level={5} style={{ marginBottom: 16, color: '#262626' }}>
                  Siparis Degeri Istatistikleri
                </Title>
              </motion.div>
            </Col>

            <Col xs={24} sm={8}>
              <motion.div variants={itemVariants}>
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Ortalama Siparis</Text>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#595959', marginTop: 4 }}>
                    &#x20BA;
                    <CountUp
                      end={data?.averageOrderValue || 0}
                      duration={1.2}
                      decimals={2}
                      separator=","
                      decimal="."
                    />
                  </div>
                </div>
              </motion.div>
            </Col>

            <Col xs={24} sm={8}>
              <motion.div variants={itemVariants}>
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    <TrophyOutlined style={{ color: '#d97706', marginRight: 4 }} />
                    En Yuksek
                  </Text>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#059669', marginTop: 4 }}>
                    &#x20BA;
                    <CountUp
                      end={data?.highestOrderAmount || 0}
                      duration={1.2}
                      decimals={2}
                      separator=","
                      decimal="."
                    />
                  </div>
                </div>
              </motion.div>
            </Col>

            <Col xs={24} sm={8}>
              <motion.div variants={itemVariants}>
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>En Dusuk</Text>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#595959', marginTop: 4 }}>
                    &#x20BA;
                    <CountUp
                      end={data?.lowestOrderAmount || 0}
                      duration={1.2}
                      decimals={2}
                      separator=","
                      decimal="."
                    />
                  </div>
                </div>
              </motion.div>
            </Col>

            {/* Fiyatli/Fiyatsiz siparisler */}
            <Col span={24}>
              <motion.div variants={itemVariants}>
                <Divider style={{ margin: '16px 0' }} />
                <Title level={5} style={{ marginBottom: 16, color: '#262626' }}>
                  Siparis Fiyatlandirma Durumu
                  <InfoCircleOutlined
                    style={{ marginLeft: 8, fontSize: 14, color: '#94a3b8' }}
                  />
                </Title>
              </motion.div>
            </Col>

            <Col span={24}>
              <motion.div variants={itemVariants}>
                <Row gutter={16} style={{ marginBottom: 12 }}>
                  <Col span={12}>
                    <div style={{
                      padding: '8px 12px',
                      backgroundColor: '#f6ffed',
                      borderRadius: 6,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Text style={{ fontSize: 13 }}>Fiyatlandirilmis</Text>
                      <Text strong style={{ color: '#059669' }}>{data?.ordersWithValue || 0}</Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{
                      padding: '8px 12px',
                      backgroundColor: '#fff2e8',
                      borderRadius: 6,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Text style={{ fontSize: 13 }}>Fiyatlandirilmamis</Text>
                      <Text strong style={{ color: '#fa8c16' }}>{data?.ordersWithoutValue || 0}</Text>
                    </div>
                  </Col>
                </Row>
                {(data?.ordersWithValue || data?.ordersWithoutValue) ? (
                  <div className="progress-animated">
                    <Progress
                      percent={Math.round(
                        (data.ordersWithValue / (data.ordersWithValue + data.ordersWithoutValue)) * 100
                      )}
                      strokeColor={{
                        '0%': '#059669',
                        '100%': '#389e0d'
                      }}
                      trailColor="#fff2e8"
                      format={(percent) => (
                        <span style={{ color: '#059669', fontWeight: 500 }}>
                          %{percent}
                        </span>
                      )}
                    />
                  </div>
                ) : (
                  <Progress percent={0} />
                )}
              </motion.div>
            </Col>
          </Row>
        </motion.div>
      </Card>
    </motion.div>
  );
};

export default FinancialSummary;
